import { searchPhotosApi } from '@/api-calls/unsplash/get'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from '@blogging/ui'
import { useFetch } from '@ngtrvu/use-api'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

const chunkArray = (array: any[], size: number) => {
  if (!array) return []
  return array.reduce((result, item, index) => {
    const chunkIndex = Math.floor(index / size)
    if (!result[chunkIndex]) {
      result[chunkIndex] = []
    }
    result[chunkIndex].push(item)
    return result
  }, [])
}

export type UnsplashImage = {
  id: string
  slug: string
  alt_description: string
  urls: {
    raw: string
    full: string
    regular: string
    small: string
    thumb: string
  }
  width: number
  height: number
  description: string
  links: {
    html: string
    download_location: string
  }
  user: {
    name: string
    username: string
    links: {
      html: string
    }
  }
}
export const UnsplashForm = ({
  onSelect,
  open,
  onOpenChange,
  showTrigger = true,
}: {
  onSelect?: (photo: UnsplashImage) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  showTrigger?: boolean
}) => {
  const { siteId } = useParams<{ siteId: string }>()
  const [query, setQuery] = useState('')
  const [internalOpen, setInternalOpen] = useState(false)

  // Controlled when `open`/`onOpenChange` are provided, otherwise standalone
  // with its own trigger button (feature image flow).
  const isOpen = open ?? internalOpen
  const setIsOpen = onOpenChange ?? setInternalOpen

  const {
    data,
    isLoading,
    isError: isErrorPhotos,
    fetch: fetchPhotos,
  } = useFetch(searchPhotosApi, {
    autoLoad: true,
    initialParams: {
      siteId,
      query,
      page: 1,
      per_page: 24,
    },
  })

  const { results: photos, total_pages, page } = data || {}

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" onClick={() => setIsOpen(true)}>
            Unsplash
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="w-[calc(100vw-50px)] h-[calc(100vh-50px)]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-row rounded-lg cursor-pointer gap-4 my-4">
              <div className="justify-center">
                <h2 className="text-lg font-bold">Unsplash</h2>
              </div>
              <Input
                className="w-full font-normal"
                placeholder="Search Unsplash"
                name="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    fetchPhotos({ siteId, query })
                  }
                }}
              />
              <Button
                onClick={() => {
                  console.log('onSearch', query)
                  fetchPhotos({ siteId, query })
                }}
                disabled={isLoading}
              >
                Search {isLoading ? '...' : ''}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex flex-col w-full rounded-lg cursor-pointer">
            <p className="text-center">Loading...</p>
          </div>
        ) : isErrorPhotos ? (
          <div className="flex flex-col w-full rounded-lg cursor-pointer">
            <p>Error loading photos</p>
          </div>
        ) : (
          <div className="overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {chunkArray(photos || [], 3).map(
                (chunk: UnsplashImage[], index: number) => {
                  return (
                    <div key={index} className="grid gap-4">
                      {chunk.map((photo: UnsplashImage) => (
                        <img
                          key={photo.id}
                          className="w-full rounded-xl shadow cursor-pointer"
                          src={photo.urls.small}
                          alt={photo.alt_description}
                          onClick={() => {
                            onSelect?.(photo)
                            // close the dialog
                            setIsOpen(false)
                          }}
                        />
                      ))}
                    </div>
                  )
                },
              )}
            </div>
            {total_pages > 1 && (
              <div className="flex justify-center mt-16">
                <Button
                  onClick={() => {
                    fetchPhotos({
                      siteId,
                      query,
                      page: page + 1,
                    })
                  }}
                >
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
